/**
 * useCitationResolvers — fetches per-citation `{ url, summary }` for
 * rendering a CitationChip with real metadata instead of the truncated
 * id fallback.
 *
 * Resolution is lazy and parallel: when the hook receives `citations`,
 * it kicks off one fetch per unique (sourceType, sourceId, partition)
 * triple, populates a map as responses arrive, and returns sync
 * `resolveUrl` + `resolveSummary` callbacks suitable for
 * `spliceCitationsIntoText({ resolveUrl, resolveSummary })`.
 *
 * Workspace scoping is enforced server-side via the auth middleware on
 * `/api/citations/resolve`; the hook just fans out the lookups.
 */
import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import type { Citation } from "@yagni/shared";

interface ResolvedCitation {
  url: string | null;
  summary: string | null;
}

function citationKey(c: Citation): string {
  if (c.sourceType === "external_object") {
    return `external_object|${c.sourcePartition ?? ""}|${c.sourceId}`;
  }
  return `${c.sourceType}||${c.sourceId}`;
}

async function fetchOne(c: Citation): Promise<ResolvedCitation> {
  const params = new URLSearchParams({
    source_type: c.sourceType,
    source_id: c.sourceId,
  });
  if (c.sourcePartition) params.set("source_partition", c.sourcePartition);
  try {
    const res = await fetch(`/api/citations/resolve?${params.toString()}`, {
      credentials: "include",
    });
    if (!res.ok) return { url: null, summary: null };
    const body = (await res.json()) as ResolvedCitation;
    return {
      url: typeof body.url === "string" ? body.url : null,
      summary: typeof body.summary === "string" ? body.summary : null,
    };
  } catch {
    return { url: null, summary: null };
  }
}

export interface CitationResolvers {
  resolveUrl: (c: Citation) => string | null;
  resolveSummary: (c: Citation) => string | null;
}

export function useCitationResolvers(citations: Citation[]): CitationResolvers {
  const [resolved, setResolved] = useState<Map<string, ResolvedCitation>>(
    () => new Map(),
  );
  // Track which keys we've already started fetching so re-renders don't
  // spam the resolver endpoint.
  const inflightRef = useRef<Set<string>>(new Set());

  // Stable key list — re-runs only when the citation set changes.
  const uniqueCitations = useMemo(() => {
    const seen = new Map<string, Citation>();
    for (const c of citations) {
      const key = citationKey(c);
      if (!seen.has(key)) seen.set(key, c);
    }
    return Array.from(seen.entries());
  }, [citations]);

  useEffect(() => {
    let cancelled = false;
    for (const [key, citation] of uniqueCitations) {
      if (resolved.has(key)) continue;
      if (inflightRef.current.has(key)) continue;
      inflightRef.current.add(key);
      void fetchOne(citation).then((result) => {
        if (cancelled) return;
        setResolved((prev) => {
          if (prev.has(key)) return prev;
          const next = new Map(prev);
          next.set(key, result);
          return next;
        });
      });
    }
    return () => {
      cancelled = true;
    };
  }, [uniqueCitations, resolved]);

  const resolveUrl = useCallback(
    (c: Citation) => resolved.get(citationKey(c))?.url ?? null,
    [resolved],
  );
  const resolveSummary = useCallback(
    (c: Citation) => resolved.get(citationKey(c))?.summary ?? null,
    [resolved],
  );

  return { resolveUrl, resolveSummary };
}
